import { useQuery } from '@tanstack/react-query';
import { QUERY_KEY } from '../../../../services/queryEndPoint';
import { fetchLeagueWaivers } from '../api/waiverApi';
import './WaiverSettingsSection.scss';

interface Props {
  leagueId: string | null;
  waiverRequired: boolean;
  waiverId: string | null;
  onWaiverRequiredChange: (value: boolean) => void;
  onWaiverIdChange: (value: string) => void;
  error?: string;
}

/**
 * "Waiver Settings" box — a toggle to require players to sign a waiver before playing, plus the
 * league waiver form to attach when it's on. Shared by the tournament create stepper
 * (TournamentDetails) and the update modal (UpdateTournamentModal), which manage the two values
 * differently (react-hook-form vs local useState) — this component stays a plain controlled
 * component so either can drive it.
 */
export default function WaiverSettingsSection({
  leagueId,
  waiverRequired,
  waiverId,
  onWaiverRequiredChange,
  onWaiverIdChange,
  error,
}: Props) {
  const { data: waivers } = useQuery({
    queryKey: [QUERY_KEY.leagueWaivers, leagueId, 'waivers'],
    queryFn: () => (leagueId ? fetchLeagueWaivers(leagueId) : Promise.resolve([])),
    enabled: !!leagueId,
    staleTime: 60 * 1000,
  });

  return (
    <div className="waiver-settings-box">
      <div className="waiver-settings-header">
        <p className="waiver-settings-title">Waiver Settings</p>
        <label className="waiver-settings-toggle">
          <span className="waiver-settings-toggle-label">
            Require players to complete waiver for this tournament
          </span>
          <span className="waiver-settings-switch">
            <input
              type="checkbox"
              checked={waiverRequired}
              onChange={(e) => onWaiverRequiredChange(e.target.checked)}
            />
            <span className="waiver-settings-slider" />
          </span>
        </label>
      </div>

      {waiverRequired && (
        <div className="waiver-settings-form-group">
          <label>Select Waiver Form</label>
          <select
            className={`waiver-settings-select ${error ? 'field-error' : ''}`}
            value={waiverId ?? ''}
            onChange={(e) => onWaiverIdChange(e.target.value)}
          >
            <option value="" disabled>
              Select a waiver form
            </option>
            {(waivers ?? []).map((w) => (
              <option key={w.waiverId} value={w.waiverId}>
                {w.name}
              </option>
            ))}
          </select>
          {error && <p className="err-msg">{error}</p>}
          <p className="waiver-settings-hint">
            All players added to this tournament must complete the selected waiver before
            participating in matches.
          </p>
        </div>
      )}
    </div>
  );
}
